import { z } from "zod/v4";

import { OfferDtoSchema } from "./offers";
import { OnsiteEditDtoSchema } from "./onsite-edits";
import {
  CreateOnsiteInjectionDtoSchema,
  OnsiteInjectionDtoSchema,
} from "./onsite-injections";
import { RedirectDtoSchema } from "./redirects";
import {
  CreateShippingRateGroupDtoSchema,
  ShippingRateGroupDtoSchema,
} from "./shipping-rate-groups";
import { UserInterfaceDtoSchema } from "./user-interfaces";

export const PriceChangeUnitList = ["percent", "dollar"] as const;
export type PriceChangeUnit = (typeof PriceChangeUnitList)[number];
export const PriceChangeRoundingList = [
  "none",
  "nearest",
  "up",
  "down",
] as const;
export type PriceChangeRounding = (typeof PriceChangeRoundingList)[number];
export const PriceDirectionList = ["increase", "decrease"] as const;
export type PriceDirection = (typeof PriceDirectionList)[number];
export interface PriceTransform {
  change: number;
  changeUnit: PriceChangeUnit;
  roundingType?: PriceChangeRounding;
  roundingAmount?: number;
}

export const VariationDtoSchema = z.object({
  id: z.uuid().describe("Unique identifier for the variation"),
  experienceId: z.uuid().describe("Experience this variation belongs to"),
  name: z.string().describe("Variation name (e.g., 'Control', 'Variant A')"),
  percentage: z
    .number()
    .describe("Traffic percentage (0-100) allocated to this variation"),
  isControl: z
    .boolean()
    .default(false)
    .describe("Whether this is the control/baseline variation"),
  order: z.number().int().describe("Display order in analytics dashboard"),
  priceChange: z
    .string()
    .nullish()
    .describe(
      "Amount to adjust prices by (positive = increase, negative = discount)"
    ),
  priceChangeUnit: z
    .enum(PriceChangeUnitList)
    .nullish()
    .describe("Whether priceChange is 'percent' or 'dollar' amount"),
  priceRoundingType: z
    .enum(PriceChangeRoundingList)
    .nullish()
    .describe("How to round final prices: 'none', 'nearest', 'up', 'down'"),
  priceRoundingAmount: z
    .string()
    .nullish()
    .describe("Amount to round to (e.g., '0.99' for $X.99 pricing)"),
  onsiteEdits: OnsiteEditDtoSchema.array()
    .optional()
    .default([])
    .describe("Content edits to apply for this variation"),
  onsiteInjections: OnsiteInjectionDtoSchema.nullish().describe(
    "CSS/JS injections for this variation"
  ),
  offer: OfferDtoSchema.nullish().describe("Discount offer configuration"),
  offerId: z.uuid().nullish().describe("ID of associated offer"),
  shippingRateGroups: z
    .optional(ShippingRateGroupDtoSchema.array().default([]))
    .describe("Shipping rate configurations"),
  excludeProducts: z
    .boolean()
    .default(false)
    .optional()
    .describe("Whether to exclude specific products from price changes"),
  includeProducts: z
    .boolean()
    .default(false)
    .optional()
    .describe("Whether to include only specific products in price changes"),
  testExperienceId: z
    .string()
    .nullish()
    .describe("ID of test experience for advanced configurations"),
  functionSyncError: z
    .boolean()
    .default(false)
    .describe("Whether there's a sync error with external functions"),
  redirects: z
    .optional(RedirectDtoSchema.array().default([]))
    .describe("URL redirects for this variation"),
  userInterfaces: z
    .optional(UserInterfaceDtoSchema.array().default([]))
    .describe("UI widgets to display for this variation"),
});

export type VariationDto = z.infer<typeof VariationDtoSchema>;
export type VariationIdNameOrder = Pick<VariationDto, "id" | "name" | "order">;
export const ExperienceListVariationDtoSchema = VariationDtoSchema.pick({
  id: true,
  experienceId: true,
  name: true,
  percentage: true,
  isControl: true,
  order: true,
  functionSyncError: true,
  testExperienceId: true,
  offerId: true,
  redirects: true,
});

export const ExperienceListWithOffersVariationDtoSchema =
  VariationDtoSchema.pick({
    id: true,
    experienceId: true,
    name: true,
    percentage: true,
    isControl: true,
    order: true,
    functionSyncError: true,
    testExperienceId: true,
    offerId: true,
    redirects: true,
    offer: true,
    userInterfaces: true,
  });

export type ExperienceListVariationDto = z.infer<
  typeof ExperienceListVariationDtoSchema
>;

export const PatchVariationDtoSchema = z
  .object({
    experienceId: z.uuid(),
    name: z.string(),
    percentage: z.number(),
    isControl: z.boolean().default(false),
    order: z.number().int(),
    priceChange: z.string().nullish(),
    priceChangeUnit: z.enum(PriceChangeUnitList).nullish(),
    priceRoundingType: z.enum(PriceChangeRoundingList).nullish(),
    priceRoundingAmount: z.string().nullish(),
    onsiteEdits: OnsiteEditDtoSchema.array().optional().default([]),
    onsiteInjections: CreateOnsiteInjectionDtoSchema.nullish(),
    offer: OfferDtoSchema.nullish(),
    shippingRateGroups: z.optional(
      ShippingRateGroupDtoSchema.array().default([])
    ),
    excludeProducts: z.boolean().optional().default(false),
    includeProducts: z.boolean().optional().default(false),
    testExperienceId: z.string().nullish(),
    functionSyncError: z.boolean().default(false),
    redirects: z.optional(RedirectDtoSchema.array().default([])),
    userInterfaces: z.optional(UserInterfaceDtoSchema.array().default([])),
  })
  .partial()
  .extend({
    id: z.uuid(),
  });

export type PatchVariationDto = z.infer<typeof PatchVariationDtoSchema>;

export const CreateVariationDtoSchema = VariationDtoSchema.omit({
  onsiteEdits: true,
  onsiteInjections: true,
  functionSyncError: true,
}).extend({
  experienceId: z
    .uuid()
    .optional()
    .describe("Experience this variation belongs to"),
  shippingRateGroups: z
    .optional(CreateShippingRateGroupDtoSchema.array().default([]))
    .describe("Shipping rate configurations for this variation"),
  redirects: z
    .optional(RedirectDtoSchema.array().default([]))
    .describe("URL redirects specific to this variation"),
});

export type CreateVariationDto = z.infer<typeof CreateVariationDtoSchema>;
